import React, { useState, useEffect } from "react";
import { getDevices, validateDevices } from "../../services/Elv3Service";
import { useHeaders } from "../../HeadersContext";
import {
  Button,
  TextField,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import { inspectionTypeOptions } from "../../config/Elv3config";

export const Elv3FormDevice = ({
  setLocationInformation,
  setDeviceData,
  setFormData,
  setInspectionUserInput,
  onDevicesValidated,
}) => {
  const { headers } = useHeaders();
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1];
  const today = new Date().toISOString().split("T")[0];

  // Global Report Year (remains global)
  const [reportYear, setReportYear] = useState(String(currentYear));

  // --- New Global Inspection Defaults for "Apply to All" ---
  // These will be used to set default values for each device.
  const [globalInspectionType, setGlobalInspectionType] = useState("1");
  const [globalInspectionDate, setGlobalInspectionDate] = useState(""); // store raw yyyy-mm-dd

  // --- Per-Device Inspection Configurations ---
  // The keys are the DeviceID and the value is an object with:
  // { inspectionDate: raw yyyy-mm-dd, inspectionTypeKey: string }
  const [deviceInspections, setDeviceInspections] = useState({});

  // Lookup method state
  // "1": DeviceID, "2": Address, "3": BIN
  const [searchBy, setSearchBy] = useState("1");

  // Lookup input states
  const [lookupDeviceId, setLookupDeviceId] = useState("");
  const [lookupHouseNumber, setLookupHouseNumber] = useState("");
  const [lookupStreetName, setLookupStreetName] = useState("");
  const [lookupBorough, setLookupBorough] = useState("");
  const [lookupBin, setLookupBin] = useState("");

  // Lookup results and selected devices (from lookup)
  const [lookupResults, setLookupResults] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  // States to store any validation errors/success messages
  const [validationError, setValidationError] = useState(null);
  const [validationSuccess, setValidationSuccess] = useState(null);

  // Formats a date string from YYYY-MM-DD (raw) to MM/DD/YYYY for the payload
  const formatDateFromInput = (inputDate) => {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${month}/${day}/${year}`;
  };

  // Compute allowed date range based on report year
  const getDateLimits = (yearStr) => {
    const minDate = `${yearStr}-01-01`;
    const maxDate =
      Number(yearStr) === currentYear ? today : `${yearStr}-12-31`;
    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getDateLimits(reportYear);

  // --- Effect to initialize per-device inspection info when devices are selected ---
  // For any new device, if no per-device config exists, use the current global defaults.
  useEffect(() => {
    setDeviceInspections((prev) => {
      const newInspections = { ...prev };
      selectedDevices.forEach((device) => {
        if (!newInspections[device.DeviceID]) {
          newInspections[device.DeviceID] = {
            inspectionDate: globalInspectionDate,
            inspectionTypeKey: globalInspectionType,
          };
        }
      });
      return newInspections;
    });
    // We intentionally depend on selectedDevices only.
  }, [selectedDevices]);

  // --- Handlers for lookup inputs and search ---
  const handleReportYearChange = (e) => {
    setReportYear(e.target.value);
  };

  // Update lookup payload and call the external API.
  const handleLookupDevices = async () => {
    const payload = {
      ctrlShow: 1,
      reportType: "1",
      searchBy: Number(searchBy),
      bin: "",
      deviceID: "",
      houseNumber: "",
      streetName: "",
      borough: "",
      reportYear: reportYear,
      // Use globalInspectionType for the lookup payload (as before)
      inspectionType: [globalInspectionType],
    };

    if (searchBy === "1") {
      payload.deviceID = lookupDeviceId;
    } else if (searchBy === "2") {
      payload.houseNumber = lookupHouseNumber;
      payload.streetName = lookupStreetName;
      payload.borough = lookupBorough;
    } else if (searchBy === "3") {
      payload.bin = lookupBin;
    }

    try {
      const result = await getDevices(payload, headers);
      setLookupResults(result.devices || []);
      if (result.LocationInformation) {
        setLocationInformation(result.LocationInformation);
      } else if (
        result.devices &&
        result.devices.length > 0 &&
        result.devices[0].LocationInformation
      ) {
        setLocationInformation(result.devices[0].LocationInformation);
      }
      console.log("Lookup Result:", result);
    } catch (err) {
      console.error("Error during device lookup:", err);
      setValidationError({
        errorDescription:
          "An error occurred during lookup. Check console for details.",
      });
    }
  };

  // --- New Handlers for per-device inspection changes ---
  const handlePerDeviceDateChange = (deviceId, value) => {
    setDeviceInspections((prev) => ({
      ...prev,
      [deviceId]: {
        ...prev[deviceId],
        inspectionDate: value,
      },
    }));
  };

  const handlePerDeviceTypeChange = (deviceId, value) => {
    setDeviceInspections((prev) => ({
      ...prev,
      [deviceId]: {
        ...prev[deviceId],
        inspectionTypeKey: value,
      },
    }));
  };

  // Handler for the global "Apply to All" button.
  const handleApplyToAll = () => {
    setDeviceInspections((prev) => {
      const updated = { ...prev };
      selectedDevices.forEach((device) => {
        updated[device.DeviceID] = {
          inspectionDate: globalInspectionDate,
          inspectionTypeKey: globalInspectionType,
        };
      });
      return updated;
    });
  };

  // --- Validate Selected Devices ---
  const handleValidateDevices = async () => {
    setValidationError(null);
    setValidationSuccess(null);

    if (selectedDevices.length === 0) {
      setValidationError({
        errorDescription: "Please select at least one device.",
      });
      return;
    }

    // Build payload for each device using its per-device inspection configuration.
    const devicesPayload = selectedDevices.map((device) => {
      const deviceConfig = deviceInspections[device.DeviceID] || {};
      // Format the raw inspection date to MM/DD/YYYY.
      const formattedDate = formatDateFromInput(deviceConfig.inspectionDate);
      // Retrieve inspection type details from the options using the selected key.
      const inspOption = inspectionTypeOptions[deviceConfig.inspectionTypeKey];
      // Determine the cat flag based on the inspection type.
      const catFlag = inspOption?.isCat1
        ? { IsCat1: true }
        : inspOption?.id === "3" || inspOption?.id === "7"
        ? { IsCat5: true }
        : {};
      return {
        InspDate: formattedDate || "",
        InspectionType: inspOption?.type,
        InspectionTypeID: inspOption?.id,
        DeviceID: device.DeviceID,
        DeviceStatus: device.DeviceStatus,
        DeviceTypeLabel: device.DeviceTypeLabel || "Elevator",
        InspectionDate: formattedDate || "",
        DeviceGuid: device.DeviceGuid,
        ...catFlag,
      };
    });

    const payload = {
      devices: devicesPayload,
      ReportYear: reportYear,
    };

    try {
      const result = await validateDevices(payload, headers);
      console.log("Validation Response =>", result);
      if (
        result.IsSuccess === false &&
        result.ElevatorErrorList &&
        result.ElevatorErrorList.length > 0
      ) {
        setValidationError({
          errorDescription:
            result.ErrorDescription || "Validation errors were found.",
          errorList: result.ElevatorErrorList,
        });
        return;
      }
      setDeviceData(devicesPayload);
      setFormData((prev) => ({
        ...prev,
        ReportYear: reportYear,
      }));
      setInspectionUserInput(devicesPayload);
      setValidationError(null);
      setValidationSuccess("✓ Devices validated successfully!");
      if (onDevicesValidated) {
        onDevicesValidated(true);
      }
    } catch (err) {
      console.error("Error during device validation:", err);
      setValidationError({
        errorDescription:
          "An error occurred during validation. Check console for details.",
      });
    }
  };

  // Enable lookup if at least reportYear and the lookup fields are provided.
  const isDeviceLookupEnabled = () => {
    if (!reportYear) return false;
    if (searchBy === "1" && !lookupDeviceId) return false;
    if (
      searchBy === "2" &&
      (!lookupHouseNumber || !lookupStreetName || !lookupBorough)
    )
      return false;
    if (searchBy === "3" && !lookupBin) return false;
    return true;
  };

  return (
    <div className="p-4 bg-white rounded-md">
      {/* Lookup Method Section */}
      <div className="mb-4">
        <FormControl component="fieldset">
          <FormLabel component="legend">Lookup Method</FormLabel>
          <RadioGroup
            row
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
          >
            <FormControlLabel value="1" control={<Radio />} label="Device ID" />
            <FormControlLabel
              value="2"
              control={<Radio />}
              label="Building Address"
            />
            <FormControlLabel value="3" control={<Radio />} label="BIN" />
          </RadioGroup>
        </FormControl>

        {searchBy === "1" && (
          <div className="mt-2">
            <input
              type="text"
              placeholder="Enter Device ID"
              value={lookupDeviceId}
              onChange={(e) => setLookupDeviceId(e.target.value)}
              className="border border-gray-300 p-2"
            />
          </div>
        )}
        {searchBy === "2" && (
          <div className="mt-2">
            <input
              type="text"
              placeholder="Enter House Number"
              value={lookupHouseNumber}
              onChange={(e) => setLookupHouseNumber(e.target.value)}
              className="border border-gray-300 p-2 mr-2"
            />
            <input
              type="text"
              placeholder="Enter Street Name"
              value={lookupStreetName}
              onChange={(e) => setLookupStreetName(e.target.value)}
              className="border border-gray-300 p-2 mr-2"
            />
            <input
              type="text"
              placeholder="Enter Borough"
              value={lookupBorough}
              onChange={(e) => setLookupBorough(e.target.value)}
              className="border border-gray-300 p-2"
            />
          </div>
        )}
        {searchBy === "3" && (
          <div className="mt-2">
            <input
              type="text"
              placeholder="Enter BIN"
              value={lookupBin}
              onChange={(e) => setLookupBin(e.target.value)}
              className="border border-gray-300 p-2"
            />
          </div>
        )}
      </div>

      {/* Report Year (still global) */}
      <div className="mb-4">
        <label className="block mb-1">Report Year:</label>
        <select
          value={reportYear}
          onChange={handleReportYearChange}
          className="w-full p-2 bg-white rounded-md border border-gray-300"
        >
          {yearOptions.map((yr) => (
            <option key={yr} value={yr}>
              {yr}
            </option>
          ))}
        </select>
      </div>

      {/* Lookup Devices Button */}
      <div className="mb-4">
        <Button
          onClick={handleLookupDevices}
          variant="contained"
          color="primary"
          disabled={!isDeviceLookupEnabled()}
        >
          Lookup Devices
        </Button>
      </div>

      {/* Display Autocomplete for Lookup Results */}
      {lookupResults.length > 0 && (
        <div className="mb-4">
          <Autocomplete
            multiple
            options={lookupResults}
            getOptionLabel={(option) =>
              `ID: ${option.DeviceID} - ${option.DeviceTypeLabel}`
            }
            onChange={(event, newValue) => setSelectedDevices(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Devices"
                variant="outlined"
              />
            )}
          />
        </div>
      )}

      {/* New Section: Global "Apply to All" Inspection Details */}
      {selectedDevices.length > 0 && (
        <div className="mb-4 border p-4 rounded-md">
          <h3 className="text-lg font-bold mb-2">Inspection Details</h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
            <div>
              <label className="block mb-1">Inspection Type:</label>
              <select
                value={globalInspectionType}
                onChange={(e) => setGlobalInspectionType(e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              >
                {Object.entries(inspectionTypeOptions).map(
                  ([key, { type }]) => (
                    <option key={key} value={key}>
                      {type}
                    </option>
                  )
                )}
              </select>
            </div>
            <div>
              <label className="block mb-1">Inspection Date:</label>
              <input
                type="date"
                min={minDate}
                max={maxDate}
                value={globalInspectionDate}
                onChange={(e) => setGlobalInspectionDate(e.target.value)}
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="mt-4 sm:mt-0">
              <Button
                onClick={handleApplyToAll}
                variant="outlined"
                color="primary"
              >
                Apply to All
              </Button>
            </div>
          </div>

          {/* Per-Device Inspection Table */}
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border p-2 text-left">Device ID</th>
                <th className="border p-2 text-left">Inspection Date</th>
                <th className="border p-2 text-left">Inspection Type</th>
              </tr>
            </thead>
            <tbody>
              {selectedDevices.map((device) => {
                const deviceConfig = deviceInspections[device.DeviceID] || {};
                return (
                  <tr key={device.DeviceID}>
                    <td className="border p-2">{device.DeviceID}</td>
                    <td className="border p-2">
                      <input
                        type="date"
                        min={minDate}
                        max={maxDate}
                        value={deviceConfig.inspectionDate || ""}
                        onChange={(e) =>
                          handlePerDeviceDateChange(
                            device.DeviceID,
                            e.target.value
                          )
                        }
                        className="p-2 border border-gray-300 rounded-md"
                      />
                    </td>
                    <td className="border p-2">
                      <select
                        value={deviceConfig.inspectionTypeKey || ""}
                        onChange={(e) =>
                          handlePerDeviceTypeChange(
                            device.DeviceID,
                            e.target.value
                          )
                        }
                        className="p-2 border border-gray-300 rounded-md"
                      >
                        {Object.entries(inspectionTypeOptions).map(
                          ([key, { type }]) => (
                            <option key={key} value={key}>
                              {type}
                            </option>
                          )
                        )}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Validate Devices Button */}
      <div className="flex gap-4">
        <Button
          onClick={handleValidateDevices}
          variant="contained"
          color="primary"
          disabled={selectedDevices.length === 0}
        >
          Validate Devices
        </Button>
      </div>

      {/* Display Validation Success or Errors */}
      {validationSuccess && (
        <div className="mt-4 text-green-600">
          <p>{validationSuccess}</p>
        </div>
      )}
      {validationError && (
        <div className="mt-4 text-red-600">
          {validationError.errorList &&
            validationError.errorList.length > 0 && (
              <ul>
                {validationError.errorList.map((error, index) => (
                  <li key={index}>
                    Device ID: {error.DeviceID}, Inspection Type:{" "}
                    {error.InspectionTypeValue}
                  </li>
                ))}
              </ul>
            )}
          <p>{validationError.errorDescription}</p>
        </div>
      )}
    </div>
  );
};
